import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import OpeningStock from "@/pages/OpeningStock";
import InwardList from "@/pages/InwardList";
import InwardNew from "@/pages/InwardNew";
import InwardDetail from "@/pages/InwardDetail";
import LiveStock from "@/pages/LiveStock";
import Drums from "@/pages/Drums";
import DrumDetail from "@/pages/DrumDetail";
import Ledger from "@/pages/Ledger";
import Masters from "@/pages/Masters";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import Cutting from "@/pages/Cutting";
import CuttingDetail from "@/pages/CuttingDetail";
import DispatchList from "@/pages/DispatchList";
import DispatchNew from "@/pages/DispatchNew";
import DispatchDetail from "@/pages/DispatchDetail";
import Outward from "@/pages/Outward";
import PrintDoc from "@/pages/PrintDoc";
import Register from "@/pages/Register";
import Reports from "@/pages/Reports";
import EmailReports from "@/pages/EmailReports";
import Counts from "@/pages/Counts";
import CountSheet from "@/pages/CountSheet";
import Adjustments from "@/pages/Adjustments";
import Valuation from "@/pages/Valuation";
import Tally from "@/pages/Tally";
import AskAI from "@/pages/AskAI";
import Insights from "@/pages/Insights";
import { QuotationList, QuotationNew } from "@/pages/Quotations";
import PartyPending from "@/pages/PartyPending";
import QrStickers from "@/pages/QrStickers";
import PublicStock from "@/pages/PublicStock";

function Protected({ children, perm }) {
  const { user, can } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground" data-testid="auth-loading">Checking session…</div>;
  if (!user) return <Navigate to={loc.pathname === "/" ? "/stock-view" : "/login"} state={{ from: loc.pathname }} replace />;
  if (user.must_change_password && loc.pathname !== "/settings") return <Navigate to="/settings" replace />;
  if (perm && !can(perm)) return <div className="p-8 text-sm" data-testid="forbidden">You do not have access to this page.</div>;
  return children;
}

function LoginGate() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginGate />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/stock-view" element={<PublicStock />} />
          <Route path="/print/:type/:id" element={<Protected><PrintDoc /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<Products />} />
            <Route path="opening-stock" element={<OpeningStock />} />
            <Route path="inward" element={<InwardList />} />
            <Route path="inward/new" element={<Protected perm="inward:create"><InwardNew /></Protected>} />
            <Route path="inward/:id" element={<InwardDetail />} />
            <Route path="live-stock" element={<LiveStock />} />
            <Route path="drums" element={<Drums />} />
            <Route path="drums/:id" element={<DrumDetail />} />
            <Route path="ledger" element={<Ledger />} />
            <Route path="customers" element={<Masters kind="customers" key="customers" />} />
            <Route path="suppliers" element={<Masters kind="suppliers" key="suppliers" />} />
            <Route path="employees" element={<Masters kind="employees" key="employees" />} />
            <Route path="admin" element={<Protected perm="users:manage"><Admin /></Protected>} />
            <Route path="settings" element={<Settings />} />
            <Route path="cutting" element={<Protected perm="cutting:create"><Cutting /></Protected>} />
            <Route path="cutting/:id" element={<CuttingDetail />} />
            <Route path="outward" element={<Outward />} />
            <Route path="dispatch" element={<DispatchList />} />
            <Route path="dispatch/new" element={<Protected perm="dispatch:create"><DispatchNew /></Protected>} />
            <Route path="dispatch/:id" element={<DispatchDetail />} />
            <Route path="register" element={<Register />} />
            <Route path="reports" element={<Reports />} />
            <Route path="email-reports" element={<Protected perm="settings:write"><EmailReports /></Protected>} />
            <Route path="reconciliation" element={<Counts />} />
            <Route path="reconciliation/:id" element={<CountSheet />} />
            <Route path="adjustments" element={<Adjustments />} />
            <Route path="valuation" element={<Protected perm="cost:view"><Valuation /></Protected>} />
            <Route path="tally" element={<Protected perm="tally:export"><Tally /></Protected>} />
            <Route path="ask" element={<AskAI />} />
            <Route path="insights" element={<Insights />} />
            <Route path="quotations" element={<Protected perm="quote:write"><QuotationList /></Protected>} />
            <Route path="quotations/new" element={<Protected perm="quote:write"><QuotationNew /></Protected>} />
            <Route path="party-pending" element={<PartyPending />} />
            <Route path="qr-stickers" element={<QrStickers />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
